import orderBy from 'lodash-es/orderBy';
import { ChannelSearchInput } from 'pages/Conversations/interfaces/ChannelSearchInput';
import { useMemo, useState } from 'react';
import { PersistedStateKeys } from 'shared/enums';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import usePersistedState from 'shared/hooks/usePersistedState';
import {
  ChannelStatusAction,
  ChannelType,
  SortOrder,
  TeletriageConversationFragment,
  useGetTeletriageConversationsQuery,
  useSubscribeToTeletriageConversationsSubscription,
} from 'shared/types/graphql';
import { DEFAULT_CHANNEL_LIST_PAGE_SIZE } from '../../ChannelList';
import { useTeletriageClinicData } from './useTeletriageClinicData';

interface TeletriageFilters {
  conversationStatus: string[];
  clinicIds: string[];
  showArchived: boolean;
}

export interface TeletriageFilterProps {
  filters: TeletriageFilters;
  setFilters: React.Dispatch<React.SetStateAction<TeletriageFilters>>;
  clearFilters: () => void;
  toggleShowArchived: () => void;
}

export interface TeletriageSearchProps {
  isSearching: boolean;
  searchInput?: ChannelSearchInput;
  setSearchInput: (searchInput: ChannelSearchInput) => void;
}

export type TeletriageSearchAndFilter = TeletriageSearchProps & TeletriageFilterProps;

export const defaultTeletriageFilters: TeletriageFilters = {
  conversationStatus: [],
  clinicIds: [],
  showArchived: false,
};

interface UseTeletriageConversationListInput {
  isDrawerOpen: boolean;
}

interface TeletriageConversationListProps {
  isLoading: boolean;
  conversations: TeletriageConversationFragment[];
  totalConversationsCount: number;
  sortByLastMessageEnabled: boolean;
  hasLoadedInitialList: boolean;
  loadMoreConversations: () => void;
  isSearchingOpen: boolean;
  toggleSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

type UseTeletriageConversationListOutput = TeletriageConversationListProps & TeletriageSearchAndFilter;

export const useTeletriageConversationList = ({
  isDrawerOpen,
}: UseTeletriageConversationListInput): UseTeletriageConversationListOutput => {
  const { careClinicsIds: clinicIds, teletriageStatusNames, teletriageStatuses, isLoading } = useTeletriageClinicData();
  const { isFeatureEnabled } = useFeatureFlag();
  const sortByLastMessageEnabled = isFeatureEnabled(FeatureFlagName.SortConversationByLastMessage);
  const [hasLoadedInitialList, setHasLoadedInitialList] = useState(false);
  const [filters, setFilters] = usePersistedState<TeletriageFilters>(
    PersistedStateKeys.TeletriageFilters,
    defaultTeletriageFilters,
  );
  const [searchInput, setSearchInput] = useState<ChannelSearchInput>({});
  const [isSearchingOpen, toggleSearch] = useState(false);

  const activeTeletriageStatusNames = useMemo(
    () =>
      teletriageStatuses.filter((ts) => ts.channelStatusAction === ChannelStatusAction.Active).map((ts) => ts.name) ||
      [],
    [teletriageStatuses],
  );

  // Data fetching
  const {
    data: conversationsData,
    loading: isLoadingConversations,
    refetch: refetchTeletriageConversationsData,
    fetchMore: fetchMoreTeletriageConversations,
  } = useGetTeletriageConversationsQuery({
    variables: {
      where: {
        channelType: ChannelType.Client,
        isActive: true,
        clinicIds: filters.clinicIds,
        channelStatusNames:
          !filters.showArchived && !filters.conversationStatus.length
            ? activeTeletriageStatusNames
            : filters.conversationStatus,
        ...(!!searchInput?.petNames?.length && { petNames: searchInput.petNames }),
        ...(!!searchInput?.petParentNames?.length && { petParentNames: searchInput.petParentNames }),
        ...(!!searchInput?.messageBodies?.length && { messageBodies: searchInput.messageBodies }),
      },
      take: DEFAULT_CHANNEL_LIST_PAGE_SIZE,
      skip: 0,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onCompleted: () => {
      if (!hasLoadedInitialList) {
        setHasLoadedInitialList(true);
      }
    },
    skip: !isDrawerOpen || isLoading,
    pollInterval: 30 * 1000,
  });

  useSubscribeToTeletriageConversationsSubscription({
    variables: {
      where: {
        clinicIds,
        channelStatusNames: teletriageStatusNames,
      },
    },
    skip: !clinicIds.length || !isDrawerOpen,
    onData: () => {
      refetchTeletriageConversationsData();
    },
  });

  const loadMoreConversations = (): void => {
    fetchMoreTeletriageConversations({
      variables: {
        skip: conversations.length,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newChannels = fetchMoreResult.teletriageChannelSearch.channels;
        return {
          teletriageChannelSearch: {
            ...fetchMoreResult.teletriageChannelSearch,
            channels: [...previousResult.teletriageChannelSearch.channels, ...newChannels],
          },
        };
      },
    });
  };

  // Filters functionality
  const clearFilters = (): void => {
    setFilters(defaultTeletriageFilters);
  };

  // Archived functionality
  const toggleShowArchived = (): void => {
    setFilters((prevFilters) => ({ ...prevFilters, showArchived: !prevFilters.showArchived }));
  };

  // Search functionality
  const isSearching = useMemo(() => {
    return (
      !isLoadingConversations &&
      (!!searchInput.messageBodies?.length || !!searchInput.petNames?.length || !!searchInput.petParentNames?.length)
    );
  }, [
    isLoadingConversations,
    searchInput.messageBodies?.length,
    searchInput.petNames?.length,
    searchInput.petParentNames?.length,
  ]);

  // Data transformation
  const totalConversationsCount = useMemo(
    () => conversationsData?.teletriageChannelSearch.total || 0,
    [conversationsData?.teletriageChannelSearch.total],
  );

  const conversations = useMemo(() => {
    const _conversations = conversationsData?.teletriageChannelSearch.channels || [];
    const sortBy = (conversation: TeletriageConversationFragment): Date =>
      sortByLastMessageEnabled ? conversation.lastMessage?.createdAt || conversation.updatedAt : conversation.updatedAt;

    return orderBy(_conversations, sortBy, SortOrder.Desc);
  }, [conversationsData?.teletriageChannelSearch.channels, sortByLastMessageEnabled]);

  return {
    isLoading: isLoadingConversations || isLoading,
    isSearching,
    filters,
    setFilters,
    clearFilters,
    searchInput,
    setSearchInput,
    toggleShowArchived,
    conversations,
    sortByLastMessageEnabled,
    hasLoadedInitialList,
    totalConversationsCount,
    loadMoreConversations,
    isSearchingOpen,
    toggleSearch,
  };
};
